import { useEffect, useMemo, useRef } from 'react';
import { useBill } from '@/hooks/useBill';
import { useCalculateSplit } from '@/hooks/useCalculateSplit';
import type { Bill as BillDetailsType } from '@/interfaces/Bill';

interface ShareBillParams {
  bill?: BillDetailsType;
  baseToPay: number;
  selectedTipPercentage: number;
  numberOfSplits: number;
  payingFor: number;
  isItemsSplitActive: boolean;
  isProcessing: boolean;
}

/**
 * What this payer actually pays, and everything priced off that share:
 * the share-priced bill query, service fee, estimated cashback and signup
 * bonus eligibility.
 *
 * Two amounts on purpose:
 * - `splitAmount` — the displayed share; prices the bill query and the tip.
 *   In items mode it is the confirmed selection sum (useCalculateSplit only
 *   models equal splits); in equal mode it is the async, server-aware
 *   calculate-split result, falling back to baseToPay until it resolves.
 * - `chargeAmount` — what the payment widget charges. With an equal split
 *   active this is the synchronous baseToPay the payer confirmed, NOT the
 *   async yourShare: the two can diverge transiently (mutation in flight)
 *   and when the server's remaining amount no longer covers full shares.
 */
export const useShareBill = ({
  bill,
  baseToPay,
  selectedTipPercentage,
  numberOfSplits,
  payingFor,
  isItemsSplitActive,
  isProcessing,
}: ShareBillParams) => {
  const { yourShare, cashback } = useCalculateSplit({
    orderNumber: bill?.orderNumber || '',
    tipPercentage: selectedTipPercentage,
    numberOfCoveredSplits: payingFor,
    numberOfSplits,
    totalBillAmount: bill?.baseTotal || 0,
    isNewSplitOpen: numberOfSplits > 0, // Only consider split "open" after confirmation, not during preview
    baseToPay,
  });

  // In items mode the share is the confirmed selection sum; useCalculateSplit's
  // yourShare only models equal splits (it falls back to the full bill total)
  const splitAmount = isItemsSplitActive ? baseToPay : yourShare || baseToPay;
  const { data: billDetailsForSplit, isLoading: isLoadingBillForSplit } = useBill({
    amount: splitAmount,
    selectedTipPercentage: selectedTipPercentage,
    enabled: splitAmount > 0,
    paused: isProcessing,
  });

  const chargeAmount = numberOfSplits > 0 ? baseToPay : splitAmount;

  const signupBonusEligible = billDetailsForSplit?.signupBonusEligible || false;

  // Cashback follows what the payer actually pays: in items mode the share-priced
  // bill (useCalculateSplit's cashback only models equal splits). No selection
  // yet -> nothing payable -> no cashback (same query aliasing as the fee).
  const estimatedCashback = isItemsSplitActive
    ? (baseToPay > 0 && billDetailsForSplit?.estimatedCashback) || 0
    : cashback || bill?.estimatedCashback || 0;

  // Last resolved fee and the share it was priced for. A tip change starts a fresh
  // (uncached) share-bill query but leaves the share untouched, so its fee still
  // applies — reusing it keeps the payment widget mounted instead of withholding
  // it on every tip tap (GK-589). A changed share still waits for its own fee.
  const lastResolvedFeeRef = useRef<{ amount: number; fee: number } | null>(null);
  useEffect(() => {
    if (!isLoadingBillForSplit && billDetailsForSplit) {
      lastResolvedFeeRef.current = {
        amount: splitAmount,
        fee: billDetailsForSplit.serviceFee || 0,
      };
    }
  }, [billDetailsForSplit, isLoadingBillForSplit, splitAmount]);

  const serviceFee = useMemo(() => {
    // Joined items split with nothing selected yet: no payable share, no fee.
    // (With amount 0 the share-priced useBill aliases the full-bill query, so
    // billDetailsForSplit would otherwise leak the full bill's fee here.)
    if (isItemsSplitActive && baseToPay === 0) {
      return undefined;
    }
    if (isLoadingBillForSplit) {
      const lastResolvedFee = lastResolvedFeeRef.current;
      return lastResolvedFee?.amount === splitAmount ? lastResolvedFee.fee : undefined;
    }
    if (billDetailsForSplit) {
      return billDetailsForSplit.serviceFee || 0;
    }
  }, [billDetailsForSplit, isLoadingBillForSplit, isItemsSplitActive, baseToPay, splitAmount]);

  // The fee for this share is not known yet (first load, or the share changed)
  const isServiceFeePending = isLoadingBillForSplit && serviceFee === undefined;

  return {
    splitAmount,
    chargeAmount,
    serviceFee,
    estimatedCashback,
    signupBonusEligible,
    yourShare,
    isLoadingBillForSplit,
    isServiceFeePending,
  };
};
